import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, Platform, Modal, ScrollView, Animated } from 'react-native';
import { globalStyles } from '../styles/globalStyles';
import { getResponsiveValue } from '../styles/breakpoints';

export const AuthSelect = ({ label, value, onChange, options, placeholder }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownHeight = useRef(new Animated.Value(0)).current;
  const dropdownOpacity = useRef(new Animated.Value(0)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;

  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg']
  });

  useEffect(() => {
    Animated.parallel([
      Animated.timing(dropdownHeight, {
        toValue: isOpen ? 250 : 0,
        duration: 300,
        useNativeDriver: false
      }),
      Animated.timing(dropdownOpacity, {
        toValue: isOpen ? 1 : 0,
        duration: 300,
        useNativeDriver: false
      }),
      Animated.timing(rotateAnim, {
        toValue: isOpen ? 1 : 0,
        duration: 300,
        useNativeDriver: true
      })
    ]).start();
  }, [isOpen]);

  const handleClickOutside = (event) => {
    if (Platform.OS === 'web') {
      const dropdown = document.getElementById('custom-dropdown');
      if (dropdown && !dropdown.contains(event.target)) {
        setIsOpen(false);
      }
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, []);

  const CustomDropdown = () => (
    <View style={[
      globalStyles.inputRow,
      {
        position: 'relative',
        zIndex: isOpen ? 9999 : 1,
        ...(Platform.OS === 'web' && {
          isolation: 'isolate'
        })
      }
    ]} id="custom-dropdown">
      <Text 
        style={[
          globalStyles.authInputLabel, 
          { 
            fontSize: label.length > 10 
              ? getResponsiveValue({
                mobile: 16,
                tablet: 18,
                laptop: 20,
                desktop: 20,
              })
              : getResponsiveValue({
                mobile: 20,
                tablet: 22,
                laptop: 24,
                desktop: 24,
              }), 
            color: '#e9e9e7',
            position: 'relative',
            zIndex: isOpen ? 10000 : 2
          }
        ]}
      >
        {label}
      </Text>
      
      <TouchableOpacity
        onPress={() => {
          setIsOpen(!isOpen);
          setIsFocused(!isFocused);
        }}
        style={{
          ...globalStyles.authInput,
          borderBottomWidth: 2,
          borderBottomColor: isFocused || isOpen ? '#97ff11' : '#e9e9e7',
          paddingVertical: 10,
          width: '60%',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: 'transparent',
          zIndex: isOpen ? 10000 : 2
        }}
      >
        <Text style={{
          color: value ? '#ffffff' : 'rgba(255,255,255,0.5)',
          fontFamily: 'Computer Says No',
          fontSize: Platform.OS === 'web' ? 28 : 22,
          ...Platform.select({
            web: {
              outlineStyle: 'none',
            },
          }),
        }}>
          {options.find(option => option.value === value)?.label || placeholder}
        </Text>
        <Animated.View style={{ transform: [{ rotate }] }}>
          <Text style={{ color: '#ffffff', fontSize: 16 }}>▼</Text>
        </Animated.View>
      </TouchableOpacity>

      <Animated.View style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        width: '60%',
        height: dropdownHeight,
        opacity: dropdownOpacity,
        backgroundColor: '#000000',
        borderWidth: 1,
        borderColor: '#97ff11',
        borderRadius: 5,
        marginTop: 5,
        overflow: 'hidden',
        zIndex: 100,
        ...(Platform.OS === 'web' && {
          boxShadow: '0 4px 12px rgba(151, 255, 17, 0.2)'
        })
      }}>
        <ScrollView 
          style={[
            { flex: 1 },
            Platform.OS === 'web' && {
              zIndex: 'auto',
              '& > *': {
                zIndex: 'auto'
              }
            }
          ]}
        >
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={{
                paddingVertical: 15,
                paddingHorizontal: 20,
                borderBottomWidth: 1,
                borderBottomColor: '#333333',
                backgroundColor: value === option ? 'rgba(151, 255, 17, 0.1)' : 'transparent',
                position: 'relative',
                zIndex: 10001,
                ...(Platform.OS === 'web' && {
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(151, 255, 17, 0.05)'
                  }
                })
              }}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
                setIsFocused(false);
              }}
            >
              <Text style={{
                color: value === option.value ? '#97ff11' : '#ffffff',
                fontFamily: 'Computer Says No',
                fontSize: getResponsiveValue({
                  mobile: 16,
                  tablet: 18,
                  laptop: 20,
                  desktop: 20,
                }),
                textAlign: 'left'
              }}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </Animated.View>
    </View>
  );

  // Mobile version remains the same but with updated styling
  if (Platform.OS !== 'web') {
    return (
      <View style={globalStyles.inputRow}>
        <Text 
          style={[
            globalStyles.authInputLabel,
            { 
              fontSize: label.length > 10 
                ? getResponsiveValue({
                  mobile: 16,
                  tablet: 18,
                  laptop: 20,
                  desktop: 20,
                })
                : getResponsiveValue({
                  mobile: 20,
                  tablet: 22,
                  laptop: 24,
                  desktop: 24,
                }), 
              color: isOpen ? '#97ff11' : '#e9e9e7' 
            }
          ]}
        >
          {label}
        </Text>
        <TouchableOpacity
          style={{
            ...globalStyles.authInput,
            borderBottomColor: isOpen ? '#97ff11' : '#e9e9e7',
            borderBottomWidth: 2,
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
          }}
          onPress={() => setIsOpen(true)}
        >
          <Text style={{ 
            color: value ? '#ffffff' : 'rgba(255,255,255,0.5)',
            fontFamily: 'Computer Says No',
            fontSize: getResponsiveValue({
              mobile: 16,
              tablet: 18,
              laptop: 20,
              desktop: 20,
            })
          }}>
            {value || placeholder}
          </Text>
          <Text style={{ color: '#ffffff', fontSize: 16 }}>▼</Text>
        </TouchableOpacity>

        <Modal
          visible={isOpen}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setIsOpen(false)}
        >
          <View style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.9)',
          }}>
            <View style={{
              width: '80%',
              maxHeight: '70%',
              backgroundColor: '#000000',
              borderRadius: 10,
              padding: 20,
              borderWidth: 1,
              borderColor: '#97ff11',
            }}>
              <Text style={{
                color: '#97ff11',
                fontSize: 24,
                fontFamily: 'Computer Says No',
                marginBottom: 20,
                textAlign: 'center'
              }}>
                {label}
              </Text>
              <ScrollView>
                {options.map((option) => (
                  <TouchableOpacity
                    key={option}
                    style={{
                      paddingVertical: 15,
                      borderBottomWidth: 1,
                      borderBottomColor: '#333333',
                      backgroundColor: value === option ? 'rgba(151, 255, 17, 0.1)' : 'transparent',
                    }}
                    onPress={() => {
                      onChange(option);
                      setIsOpen(false);
                    }}
                  >
                    <Text style={{ 
                      color: value === option ? '#97ff11' : '#ffffff',
                      fontSize: 18,
                      fontFamily: 'Computer Says No',
                      textAlign: 'center'
                    }}>
                      {option}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <TouchableOpacity
                style={{
                  marginTop: 20,
                  padding: 15,
                  backgroundColor: '#333333',
                  borderRadius: 5,
                  alignItems: 'center',
                }}
                onPress={() => setIsOpen(false)}
              >
                <Text style={{ 
                  color: '#ffffff',
                  fontSize: 18,
                  fontFamily: 'Computer Says No'
                }}>
                  Cancel
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </View>
    );
  }

  return <CustomDropdown />;
}; 